import { useState, useEffect, useCallback } from "react";

export const useFilterState = (initialValues = {}, dependencies = {}) => {
  const [filters, setFilters] = useState({
    priceValues: [0, 1000],
    category: [],
    typeproduct: [],
    typearticle: [],
    brand: [],
    color: [],
    size: [],
    rating: "",
    sortPrice: "",
    pageNumber: 1,
    styles: "grid",
    filterVisible: true,
    searchValue: "",
    ...initialValues,
  });

  // Estados para expansión de secciones
  const [expandedSections, setExpandedSections] = useState({
    category: false,
    typeproduct: false,
    typearticle: false,
    brand: false,
    color: false,
    size: false,
  });

  // Effect para inicializar desde dependencias
  useEffect(() => {
    const updates = {};

    // Inicializar desde dependencias
    if (dependencies.priceRange && dependencies.priceRange.low && dependencies.priceRange.high) {
      updates.priceValues = [dependencies.priceRange.low, dependencies.priceRange.high];
    }

    if (Object.keys(updates).length > 0) {
      setFilters(prev => ({
        ...prev,
        ...updates,
      }));
    }
  }, [dependencies.priceRange]);

  // Toggle para secciones expandibles
  const toggleSection = useCallback(section => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Handler para cambio de filtros
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => {
      // Si el valor no cambia, no actualizar
      if (prev[key] === value) return prev;

      return {
        ...prev,
        [key]: value,
        // Resetear página cuando cambia algún filtro principal
        ...(key !== "pageNumber" && ["category", "typeproduct", "typearticle", "brand", "color", "size", "rating"].includes(key) && { pageNumber: 1 }),
      };
    });
  }, []);

  // Handler para checkboxes con selección múltiple
  const handleCheckboxChange = useCallback(
    filterKey => (e, value) => {
      setFilters(prev => {
        const currentValues = prev[filterKey] || [];
        let newValues;
        
        if (e.target.checked) {
          // Agregar valor si no existe
          newValues = currentValues.includes(value) ? currentValues : [...currentValues, value];
        } else {
          // Quitar valor
          newValues = currentValues.filter(v => v !== value);
        }
        
        return {
          ...prev,
          [filterKey]: newValues,
          pageNumber: 1
        };
      });
    },
    []
  );

  // Handler específico para rating
  const handleRatingChange = useCallback(
    rating => {
      updateFilter("rating", rating);
    },
    [updateFilter]
  );

  const handleResetRating = useCallback(() => {
    updateFilter("rating", "");
  }, [updateFilter]);

  // Handler específico para precio
  const handlePriceChange = useCallback(
    values => {
      updateFilter("priceValues", values);
    },
    [updateFilter]
  );

  // Resetear todos los filtros
  const resetFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      category: [],
      typeproduct: [],
      typearticle: [],
      brand: [],
      color: [],
      size: [],
      rating: "",
      priceValues: dependencies.priceRange ? [dependencies.priceRange.low, dependencies.priceRange.high] : [0, 1000],
      sortPrice: "",
      pageNumber: 1,
    }));
  }, [dependencies.priceRange]);

  // Resetear un filtro específico
  const resetFilter = useCallback(
    filterKey => {
      if (["category", "typeproduct", "typearticle", "brand", "color", "size"].includes(filterKey)) {
        updateFilter(filterKey, []);
      } else {
        updateFilter(filterKey, "");
      }
    },
    [updateFilter]
  );

  return {
    filters,
    expandedSections,
    updateFilter,
    handleCheckboxChange,
    handleRatingChange,
    handleResetRating,
    handlePriceChange,
    toggleSection,
    resetFilters,
    resetFilter,
    setExpandedSections,
  };
};
